import { useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { LOCALES, type Locale } from "@nh/shared";
import { Alert } from "@/shared/ui/core";
import { FormSection, LocaleTabs } from "@/features/admin/components";
import { PackageContentFields } from "./PackageContentFields";
import { localeFillState, type PackageFormValues } from "../packageForm";

const PANEL_ID = "package-content-panel";

const localeNames: Record<Locale, string> = {
  es: "Español",
  en: "English",
};

/**
 * El contenido del paquete en los dos idiomas.
 *
 * Una pestaña por idioma, y el estado de CADA una a la vista en la
 * pestaña misma: se ve que el inglés está a medias sin abrirlo. Cuando
 * además está a medias de verdad —hay eslogan pero no nombre— sale un
 * aviso, porque ese es el caso que la tienda en inglés enseña mezclado.
 *
 * Solo se PINTA el idioma activo, y lo escrito en el otro no se pierde:
 * react-hook-form conserva por omisión (`shouldUnregister: false`) los
 * valores de los campos que se desmontan, y los vuelve a poner al
 * montar. La alternativa —montar los dos y esconder uno con `hidden`—
 * deja dos campos "Nombre" en el documento a la vez: un lector de
 * pantalla los anuncia por duplicado y un autocompletado escribe en el
 * equivocado.
 */
export function PackageTranslationsSection() {
  const { t } = useTranslation();
  const { control } = useFormContext<PackageFormValues>();
  const [active, setActive] = useState<Locale>("es");

  const translations = useWatch({ control, name: "translations" });
  const states = {
    es: localeFillState(translations?.es ?? { name: "", tagline: "", description: "" }),
    en: localeFillState(translations?.en ?? { name: "", tagline: "", description: "" }),
  };

  return (
    <FormSection
      title={t("Contenido")}
      /*
        Sin explicación (Anahí, 26-ago). Lo que decía —que el español es
        obligatorio y el inglés puede esperar— ya lo dicen las dos cosas
        que se ven: el asterisco de obligatorio en los campos del español
        y el estado que lleva cada pestaña en el propio selector. Una
        frase que repite lo que la pantalla ya enseña solo ocupa alto.
      */
      columns={2}
      aside={
        <LocaleTabs
          panelId={PANEL_ID}
          active={active}
          onChange={(value) => setActive(value as Locale)}
          tabs={LOCALES.map((locale) => ({
            value: locale,
            label: localeNames[locale],
            state: states[locale],
          }))}
        />
      }
    >
      {states.en === "partial" && (
        <Alert variant="warning" className="sm:col-span-2">
          {t("El inglés está incompleto: escribiste algo pero falta el nombre.")}
        </Alert>
      )}

      {/*
        `key={active}` fuerza el remontaje al cambiar de idioma: sin él,
        React reutilizaría los mismos `<input>` cambiándoles el `name` por
        debajo, y el valor del idioma anterior se quedaría escrito en
        pantalla como si fuera del nuevo.
      */}
      <div
        key={active}
        id={PANEL_ID}
        role="tabpanel"
        aria-labelledby={`${PANEL_ID}-tab-${active}`}
        className="grid grid-cols-1 gap-4 sm:col-span-2 sm:grid-cols-2"
      >
        <PackageContentFields locale={active} required={active === "es"} />
      </div>
    </FormSection>
  );
}
